/**
 * The transfer queue — pure model, no React and no network, so the rules that matter can be
 * unit-tested rather than click-tested.
 *
 * ★ David asked for a queue where entries that have not started yet can be removed. That single
 * sentence carries the whole state machine below: an entry's removability depends on what has
 * happened to its BYTES, not on what the UI is showing.
 *
 * ⚠ B7 IS THE LOAD-BEARING RULE: queued and in-flight transfers are cancellable at ANY point, but
 * once a file has COMPLETED it is permanent to the provider — those bytes are a handover and may
 * be the only copy until staff collect them. The server enforces this (403 on a completed tus
 * DELETE); this model must agree with it, or the UI will offer a button that always fails.
 */

/**
 * ★★ `ready` vs `queued` is the explicit-start seam (David, 2026-07-24).
 *
 * A dropped file lands as **ready** and does NOTHING until the provider presses Start. Only then
 * does it become **queued**, and the pump takes queued entries one at a time.
 *
 * ⚠ WHY THIS MATTERS MORE HERE THAN IN AN ORDINARY UPLOADER: B7 makes a COMPLETED upload permanent
 * to the provider — no remove button, they must email us. So an accidental drop that runs to
 * completion cannot be undone by the person who made the mistake. Starting on drop made the most
 * expensive error the easiest one to commit, and "select the wrong project, then drop" is an easy
 * slip. Everything else about a mis-drop is recoverable; that isn't.
 */
export type QueueState = 'ready' | 'queued' | 'uploading' | 'complete' | 'failed' | 'cancelled'

export interface QueueEntry {
  /** Stable id for this attempt — NOT the server's file key, which only exists after creation. */
  id: string
  name: string
  size: number
  projectKey: string
  state: QueueState
  /** Bytes confirmed accepted by the server. */
  sent: number
  /** The tus upload URL, once created — this is what makes a resume possible after a reload. */
  url?: string
  /** The server's file key, once the upload completes. */
  fileKey?: string
  error?: string
  /**
   * The bytes themselves.
   *
   * ⚠ OPTIONAL AND NEVER PERSISTED. A `File` cannot survive a page reload, which is the whole
   * reason resumeStore exists: after a refresh an entry is restored WITHOUT this, and stays
   * unstartable until the provider re-picks the file. Keeping it optional is what stops the rest
   * of the model quietly assuming bytes are always available.
   */
  file?: File
}

/**
 * A newly added file — **ready**, not queued. Nothing is sent until Start.
 *
 * ⚠ The one exception is a RESUME, where the provider has already made a deliberate choice by
 * picking the interrupted file; the screen enqueues those as `queued` so they continue at once.
 */
export function makeEntry(file: File, projectKey: string, id: string): QueueEntry {
  return { id, name: file.name, size: file.size, projectKey, state: 'ready', sent: 0, file }
}

/**
 * ★★★ A TRANSFER BELONGS TO A PROJECT, AND THE SCREEN MUST SAY WHICH (David, 2026-07-24).
 *
 * The queue survives a reload, and each restored row carries the project it was dropped into. But
 * the screen shows ONE project at a time and used to render every row regardless — so a row
 * belonging to project A appeared under project B's heading, its upload correctly landed in A, and
 * B's "Uploaded files" quite correctly never showed it. That reads exactly like a lost upload, and
 * B's "Delete project" button stayed available because B genuinely held nothing.
 *
 * ⇒ Everything the Transfers panel renders is scoped through here. Nothing is HIDDEN by it — what
 * belongs to other projects is counted and announced instead (otherProjects), because a queue that
 * silently shrank would be its own kind of alarming.
 */
export function forProject(entries: readonly QueueEntry[], projectKey: string): QueueEntry[] {
  return entries.filter((e) => e.projectKey === projectKey)
}

/** What is queued elsewhere, so it can be named rather than silently omitted. */
export function otherProjects(entries: readonly QueueEntry[], projectKey: string): {
  count: number
  uploading: number
  firstProjectKey: string
} {
  const others = entries.filter((e) => e.projectKey !== projectKey && e.state !== 'complete')
  return {
    count: others.length,
    uploading: others.filter((e) => e.state === 'uploading').length,
    firstProjectKey: others[0]?.projectKey ?? '',
  }
}

/**
 * Arm every ready entry — what the Start button does. Whole batch only, by decision.
 *
 * ⚠ A row restored after a reload has no bytes yet, and arming it would move it to `queued` where
 * it would sit reading "waiting" forever with nothing to send. It stays `ready` until its file is
 * handed back — at which point the provider presses Start, exactly as they would have before.
 *
 * ⚠ SCOPED when a project is given: Start sits under one project's heading, so it must not quietly
 * begin sending files the provider cannot even see from where they are standing.
 */
export function armReady(entries: readonly QueueEntry[], projectKey?: string): QueueEntry[] {
  return entries.map((e) =>
    e.state === 'ready' && e.file !== undefined && (projectKey === undefined || e.projectKey === projectKey)
      ? { ...e, state: 'queued' as const }
      : e,
  )
}

/**
 * Entries waiting for a Start press — and able to act on one.
 *
 * ⚠ File-less restored rows are excluded on purpose: this drives the button's own label ("Start
 * transfer — 3 files, 8.1 Gb"), and counting rows that cannot move would promise a transfer that
 * does not begin.
 */
export function readyEntries(entries: readonly QueueEntry[]): QueueEntry[] {
  return entries.filter((e) => e.state === 'ready' && e.file !== undefined)
}

/** Total bytes still to be sent once Start is pressed — so the button can say what it will do. */
export function readyBytes(entries: readonly QueueEntry[]): number {
  return readyEntries(entries).reduce((n, e) => n + e.size, 0)
}

/**
 * Can the provider remove this entry themselves?
 *
 * ★ Everything except a completed upload. A FAILED or CANCELLED entry is just clutter — nothing
 * of value is lost by clearing it, and refusing would leave dead rows the provider cannot tidy.
 */
export function canRemove(entry: QueueEntry): boolean {
  return entry.state !== 'complete'
}

/**
 * Does removing this entry need the server told (tus Termination)?
 *
 * ★ Only when bytes may exist server-side. A never-started entry has no upload URL, so there is
 * nothing on disk and no request to make — DELETEing a URL that was never created would 404 and
 * read as an error to the user.
 */
export function needsServerDelete(entry: QueueEntry): boolean {
  return entry.url !== undefined && entry.state !== 'complete'
}

/**
 * ★★★ HOW TO CANCEL THIS ENTRY — and it is NOT always "abort the running upload".
 *
 * ⛔ THE BUG THIS EXISTS TO PREVENT (David, 2026-07-24, found on live): the screen used to
 * terminate only when it held a live tus handle. Handles live in a `useRef` Map, so a page reload
 * wipes them — while the entry's URL survives in localStorage. Cancel a restored row that had NOT
 * yet started in this session and the client sent NOTHING: the row vanished locally, its resume
 * record was cleared, and the server was left with an `uploading` row and its part-file that
 * nothing on earth still pointed at. It showed up as a 32MB orphan and a file stuck at
 * "In progress" in the provider's own list.
 *
 * ⚠ An in-flight upload is aborted through its HANDLE whether or not we have observed its URL yet —
 * otherwise cancelling in the first moments of a transfer leaves it running invisibly, having
 * already been removed from the queue.
 */
export type CancelAction = 'abort-handle' | 'terminate-url' | 'nothing'

export function cancelActionFor(entry: QueueEntry, hasHandle: boolean): CancelAction {
  if (hasHandle) return 'abort-handle'
  if (needsServerDelete(entry)) return 'terminate-url'
  return 'nothing'
}

/**
 * ★★ WHICH CHUNK IS ON THE WIRE (David, 2026-07-24: *"for a very large file the % display and
 * progress bar move very slowly and a chunk number display might change more frequently"*).
 *
 * He is right, and the arithmetic says how right: at 8MB chunks a 5 Gb master is ~640 chunks, so
 * ONE chunk is 0.16% of the file — the rounded percentage only moves once every six or seven
 * chunks. On a domestic line that can be a minute of a display that looks frozen, which is exactly
 * when a provider starts wondering whether to give up and reload.
 *
 * ★ DERIVED, NOT TRACKED. It comes from the same `sent` figure the percentage does, so there is no
 * second counter that could disagree with the bar — the lesson v1 taught with its `accumulator`.
 *
 * ★ It also makes a RESUME self-evident: coming back at "chunk 301 of 640" is visible proof it
 * continued rather than restarted, which the percentage alone states far less plainly.
 *
 * @param  chunkSize the transport's chunk size (passed in, so this model keeps no knowledge of tus)
 * @return null when there is nothing useful to say — not uploading, or a file that fits in one
 *         chunk, where "chunk 1 of 1" is noise dressed up as information.
 */
export function chunkProgress(entry: QueueEntry, chunkSize: number): { current: number; total: number } | null {
  if (entry.state !== 'uploading' || chunkSize <= 0 || entry.size <= 0) return null

  const total = Math.ceil(entry.size / chunkSize)
  if (total <= 1) return null

  // ⚠ `sent` includes the chunk currently in flight, so this names the one being TRANSFERRED, not
  // the last one confirmed. Clamped because a completed final chunk would otherwise read N+1.
  const current = Math.min(total, Math.floor(Math.max(0, entry.sent) / chunkSize) + 1)

  return { current, total }
}

/**
 * The percentage as the provider should read it.
 *
 * ★★★ WHEN TO SHOW DECIMALS IS DERIVED, NOT CHOSEN (David, 2026-07-24 — his rule, and it is the
 * right one): **use them once a whole CHUNK is less than 1% of the file.**
 *
 * That is precisely the point where the display stops being able to reassure. Below it, every
 * completed chunk moves the whole-number percentage at least one step, so the number visibly
 * advances as the transfer does. Above it, a chunk can land — 8 Mb safely on our disk — and the
 * screen not change at all, which is when a provider starts to wonder whether it has stalled.
 *
 * With 8 Mb chunks that lands at 100 chunks = **800 Mb** (838,860,800 bytes). ★ Expressing it as
 * the RULE rather than as that constant means it stays correct on its own if the chunk size is ever
 * retuned — the number would otherwise quietly become wrong and nothing would fail.
 *
 * ⚠ FIXED decimals, deliberately NOT the `parseFloat(toFixed(2))` trick the file-size formatter
 * uses. Dropping trailing zeros there keeps sizes tidy ("2 Mb", not "2.00 Mb"); doing it HERE would
 * make the label change width as it counted — "12.5%" then "12.53%" then "12.6%" — and a number
 * that shuffles horizontally reads as broken, which is the opposite of the reassurance this exists
 * to give.
 *
 * @param chunkSize the transport's chunk size (passed in, as with chunkProgress, so this model
 *                  keeps no knowledge of tus)
 */
export function percentLabel(entry: QueueEntry, chunkSize: number): string {
  // ⚠ Strictly less-than: at exactly 100 chunks a chunk IS 1%, so whole numbers still move.
  const chunkIsUnderOnePercent = chunkSize > 0 && chunkSize * 100 < entry.size

  return `${(progressOf(entry) * 100).toFixed(chunkIsUnderOnePercent ? 2 : 0)}%`
}

/** Progress 0..1. A zero-byte file is complete by definition rather than a division by zero. */
export function progressOf(entry: QueueEntry): number {
  if (entry.state === 'complete') return 1
  if (entry.size <= 0) return entry.state === 'queued' ? 0 : 1
  return Math.min(1, Math.max(0, entry.sent / entry.size))
}

/**
 * The next entry to start, or null.
 *
 * ⚠ ONE AT A TIME, and not for tidiness: our RequestToken rotates per request, so two uploads in
 * flight race the rotation and produce 428s (see tusUploader). The queue is what keeps that
 * invariant true across FILES, `parallelUploads: 1` keeps it true within one.
 */
export function nextToStart(entries: readonly QueueEntry[]): QueueEntry | null {
  if (entries.some((e) => e.state === 'uploading')) return null
  // ⚠ `file` must be present: a restored-after-reload entry has none until the provider re-picks
  // it, and starting it would throw rather than upload.
  return entries.find((e) => e.state === 'queued' && e.file !== undefined) ?? null
}

/** Immutable patch by id — the reducer every UI action funnels through. */
export function patchEntry(entries: readonly QueueEntry[], id: string, patch: Partial<QueueEntry>): QueueEntry[] {
  return entries.map((e) => (e.id === id ? { ...e, ...patch } : e))
}

export function removeEntry(entries: readonly QueueEntry[], id: string): QueueEntry[] {
  return entries.filter((e) => e.id !== id)
}

/**
 * Entries with bytes already on the server.
 *
 * ★ These are the ones a reload can continue from the middle; everything else in a restored queue
 * starts from zero once its file is handed back.
 */
export function resumable(entries: readonly QueueEntry[]): QueueEntry[] {
  return entries.filter((e) => e.url !== undefined && (e.state === 'uploading' || e.state === 'failed'))
}

/**
 * ★★ RESTORED AFTER A RELOAD, STILL WAITING FOR ITS BYTES (David, 2026-07-24: *"a refresh loses
 * the transfer queue — is there any way this can be retained?"*).
 *
 * The list survives a refresh; the FILES cannot. A `File` is dead the moment the page unloads and
 * no web app may re-read it without the user re-picking or a File System Access handle — so a
 * restored entry holds its name, size, order and project, and waits.
 *
 * ⚠ THIS PREDICATE IS WHY THE FEATURE IS SAFE. Everything about a restored row — how it is drawn,
 * whether the pump will touch it, what the summary says — hangs off "has no file yet", never off a
 * separate `restored` flag that could disagree with reality. There is no state in which a row looks
 * ready to send and has nothing to send.
 */
export function needsFile(entry: QueueEntry): boolean {
  return entry.file === undefined && entry.state !== 'complete' && entry.state !== 'cancelled'
}

/** Every entry waiting to be handed its file back — what the "choose them again" button acts on. */
export function awaitingFile(entries: readonly QueueEntry[]): QueueEntry[] {
  return entries.filter(needsFile)
}

/**
 * Attach a re-picked file to the entry that was waiting for it.
 *
 * ⚠ MATCHED ON NAME **AND** SIZE, exactly as a resume is (see resumeStore.fileMatches): name alone
 * would cheerfully drop a different cut of the same film into the slot. Only entries that are
 * actually waiting can be matched, so re-picking cannot disturb a transfer already running.
 *
 * ★ Returns the entry it matched (or null), so the caller can tell the provider precisely which of
 * their chosen files found a home and which did not.
 */
export function matchAwaitingFile(entries: readonly QueueEntry[], file: File): QueueEntry | null {
  return entries.find((e) => needsFile(e) && e.name === file.name && e.size === file.size) ?? null
}

/** A human summary for the queue header — what is actually happening, in one line. */
export function queueSummary(entries: readonly QueueEntry[]): string {
  const waitingForFile = entries.filter(needsFile).length
  const ready = entries.filter((e) => e.state === 'ready' && !needsFile(e)).length
  const active = entries.filter((e) => e.state === 'uploading').length
  const queued = entries.filter((e) => e.state === 'queued' && !needsFile(e)).length
  const failed = entries.filter((e) => e.state === 'failed' && !needsFile(e)).length
  const parts: string[] = []
  // ⚠ "ready" is reported separately from "waiting": one is waiting for the PROVIDER, the other
  // for the queue. Collapsing them would hide the fact that nothing is happening yet.
  if (ready) parts.push(`${ready} ready`)
  if (active) parts.push(`${active} uploading`)
  if (queued) parts.push(`${queued} waiting`)
  if (failed) parts.push(`${failed} failed`)
  // ★ Counted apart from all of the above, and last: a restored row is not "ready" in any sense
  // the provider can act on by pressing Start. It needs them to find the file first.
  // ⚠ "re-select" is the ONE verb for this action across the whole screen — the row's status,
  // its button and this summary. Two words for one thing is the confusion David called out.
  if (waitingForFile) parts.push(`${waitingForFile} to re-select`)
  return parts.join(' · ')
}
